import './BoardFilterButton.scss'

import React, { Component } from 'react'
import PropTypes from 'prop-types'
import Button from '@material-ui/core/Button'
import Popover from '@material-ui/core/Popover'
import withStyles from '@material-ui/core/styles/withStyles'
import omit from 'lodash/omit'
import sortBy from 'lodash/sortBy'
import find from 'lodash/find'
import Switch from '@material-ui/core/Switch'
import { Icon } from 'react-fa'
import DialogButton from '../DialogButton'
import AssigneeAddButton, { mapUserToSuggestion } from '../AssigneeAddButton'
import FilterRemoveButton from '../FilterRemoveButton'
import FilterList from './FilterList'

const menuPositioning = {
  anchorOrigin: { 'horizontal': 'right', 'vertical': 'bottom' },
  transformOrigin: { 'horizontal': 'right', 'vertical': 'top' }
}

let enabledStyle = theme => ({
  root: {
    backgroundColor: '#CDEBD8',
    color: '#159A3F',
    marginLeft: '20px',
    whiteSpace: 'nowrap',
    height: '34px',
    '&:hover': {
      backgroundColor: '#AEDDBF'
    }
  }
})

let EnabledButton = withStyles(enabledStyle)(Button)

let disabledStyle = theme => ({
  root: {
    backgroundColor: theme.palette.grey[200],
    marginLeft: '20px',
    whiteSpace: 'nowrap',
    height: '34px',
    '&:hover': {
      backgroundColor: '#ddd'
    }
  }
})

let DisabledButton = withStyles(disabledStyle)(Button)

const FilterButton = (props) => {
  let passedProps = omit(props, 'children', 'enabled')
  return props.enabled
    ? <EnabledButton {...passedProps}>{props.children}</EnabledButton>
    : <DisabledButton {...passedProps}>{props.children}</DisabledButton>
}

FilterButton.propTypes = {
  enabled: PropTypes.bool,
  children: PropTypes.node
}

let filterSwtichStyle = theme => ({
  track: {},
  checked: {
    color: '#009b3a',
    '& + $track': {
      backgroundColor: '#009b3a'
    }
  }
})

export const FilterSwitch = withStyles(filterSwtichStyle)(Switch)

const isFiltered = (props) => {
  return props.isUnassignedFilterOn ||
    props.isAssignedFilterOn ||
    props.filteredUsers.length > 0
}

const renderFilter = (filter) =>
  <FilterRemoveButton
    filterName={filter.item.name}
    onRemove={() => filter.onDelete(filter)} />

const isNotUser = userToFilter => user => user.id !== userToFilter.id
const isUser = userToFilter => user => user.id === userToFilter.id

class BoardFilterButton extends Component {
  constructor(props) {
    super(props)
    this.state = {
      expanded: false,
    }
  }

  onNoAssigneesChecked = event => {
    this.props.unassignedFilterChange(event.target.checked)
  }

  onAnyAssigneesChecked = event => {
    this.props.assignedFilterChange(event.target.checked)
  }

  onPopoverClose = () => {
    if (!isFiltered(this.props)) {
      this.props.filterToggleChange(false)
    }
    this.setState({ expanded: false })
  }

  onSelectUser = (user) => {
    const users = sortBy(
      this.props.filteredUsers.concat([user]),
      [
        u => u.firstName.toLowerCase(),
        u => u.lastName.toLowerCase()
      ])
    this.updateUsersFilter(users)
  }

  mapUserToFilter = (user) => ({
    item: user,
    onDelete: () => {
      const users = this.props.filteredUsers.filter(u => u.id !== user.id)
      this.updateUsersFilter(users)
    }
  })

  updateUsersFilter = (users) => {
    this.props.userFilterChange(users)
  }

  clearFilter = () => {
    this.props.clearFilter()
  }

  getPrioritySuggestions = () => {
    let prioritySuggestions = []
    let { isAssignedFilterOn, isUnassignedFilterOn, filteredUsers,
      assignedFilterChange, unassignedFilterChange, localize, userInfo } = this.props

    if (!isAssignedFilterOn) {
      prioritySuggestions.push({
        id: 'WITH_ASSIGNEES',
        value: localize('taskBoards.Text_WithAssignees'),
        onSelect: () => assignedFilterChange(true)
      })
    }

    if (!isUnassignedFilterOn) {
      prioritySuggestions.push({
        id: 'WITH_NO_ASSIGNEES',
        value: localize('taskBoards.Text_WithNoAssignees'),
        onSelect: () => unassignedFilterChange(true)
      })
    }

    if (!filteredUsers.some(user => user.id === userInfo.id)) {
      prioritySuggestions.push(mapUserToSuggestion(userInfo, this.onSelectUser))
    }

    if (prioritySuggestions.length) {
      prioritySuggestions[prioritySuggestions.length - 1].classes =
        [ 'react-autosuggest__suggestion-content--last-priority-suggestion' ]
    }

    return prioritySuggestions
  }

  getAppliedFilters = () => {
    let appliedFilters = []
    let { isAssignedFilterOn, isUnassignedFilterOn, filteredUsers,
      assignedFilterChange, unassignedFilterChange, localize, userInfo } = this.props

    if (isAssignedFilterOn) {
      appliedFilters.push({
        item: {
          id: 'WITH_ASSIGNEES',
          name: localize('taskBoards.Text_WithAssignees')
        },
        onDelete: () => assignedFilterChange(false),
        render: renderFilter
      })
    }

    if (isUnassignedFilterOn) {
      appliedFilters.push({
        item: {
          id: 'WITH_NO_ASSIGNEES',
          name:  localize('taskBoards.Text_WithNoAssignees')
        },
        onDelete: () => unassignedFilterChange(false),
        render: renderFilter
      })
    }

    let currentUser = find(filteredUsers, isUser(userInfo))
    if (currentUser) {
      appliedFilters.push(this.mapUserToFilter(currentUser))
    }

    return appliedFilters.concat(
      filteredUsers
        .filter(isNotUser(userInfo))
        .map(this.mapUserToFilter)
    )
  }

  render() {
    const anchorRefName = 'board-filter-anchor'
    const { localize, filteredUsers, userInfo } = this.props

    return (
      <React.Fragment>
        <FilterButton
          enabled={this.props.isFilteringEnabled}
          onClick={() => this.setState(prevState => ({ expanded: !prevState.expanded }))}
          className='board-filter-button'
          disableRipple
          disableFocusRipple>
          <span>
            <Icon className='board-filter-button__filter-icon' name='filter' />
            <span className='board-filter-button__label'>
              {
                localize('common.Text_Filter').toUpperCase()} {
                this.props.isFilteringEnabled
                  ? localize('common.Label_On').toUpperCase()
                  : localize('common.Label_Off').toUpperCase()
              }
            </span>
          </span>
          <Icon
            className='board-filter-button__caret-icon'
            name={this.state.expanded ? 'caret-up' : 'caret-down'} />
          <div ref={anchorRefName} className='board-filter-anchor' />
        </FilterButton>
        <Popover
          onClose={this.onPopoverClose}
          open={this.state.expanded}
          className='board-filter-popover'
          anchorEl={() => (this.refs[anchorRefName])}
          anchorOrigin={menuPositioning.anchorOrigin}
          anchorPosition={{ top: 200, left: 200 }}
          transformOrigin={menuPositioning.transformOrigin}>
          <div className='board-filter-popover__header'>
            <div className='board-filter-popover__switch'>
              <FilterSwitch
                checked={this.props.isFilteringEnabled}
                onChange={(event, checked) => this.props.filterToggleChange(checked)} />
            </div>
            <div className='board-filter-popover__title'>{localize('taskBoards.Label_FilterTasksBy')}</div>
          </div>
          <div className='board-filter-popover__body'>
            <div className='assignee-add'>
              <div className='assignee-list__label'>{localize('tasks.Label_Assignees')}</div>
              <AssigneeAddButton
                id='filter__add-assignee'
                menuDirection='left'
                currentAssignments={filteredUsers.concat([{ id: userInfo.id }])}
                onSelect={this.onSelectUser}
                prioritySuggestions={this.getPrioritySuggestions()}
                keepOpenOnSelect
                />
            </div>
            <div className='assignee-list'>
              <FilterList
                filters={this.getAppliedFilters()}
                />
            </div>
          </div>
          <div className='board-filter-popover__footer'>
            <DialogButton
              color='primary'
              className='board-filter-popover__clear-filter-button'
              onClick={this.clearFilter}
              disableRipple
              disableFocusRipple
              disabled={!isFiltered(this.props)}>{localize('taskBoards.Label_ClearFilter').toUpperCase()}</DialogButton>
          </div>
        </Popover>
      </React.Fragment>
    )
  }
}

BoardFilterButton.propTypes = {
  userFilterChange: PropTypes.func.isRequired,
  unassignedFilterChange: PropTypes.func.isRequired,
  assignedFilterChange: PropTypes.func.isRequired,
  filterToggleChange: PropTypes.func.isRequired,
  clearFilter: PropTypes.func.isRequired,
  filteredUsers: PropTypes.array.isRequired,
  isFilteringEnabled: PropTypes.bool,
  isAssignedFilterOn: PropTypes.bool,
  isUnassignedFilterOn: PropTypes.bool,
  localize: PropTypes.func.isRequired,
  userInfo: PropTypes.object.isRequired
}

export default BoardFilterButton



// WEBPACK FOOTER //
// ./src/components/BoardFilterButton/BoardFilterButton.js